import { useEffect, useRef, useState } from 'react'
import { apiClient, normalizeApiError } from '../../lib/apiClient'
import type { PatientFile } from '../appointments/appointment.types'

export default function PrivateFilePreview({ file, onClose }: { file: PatientFile; onClose: () => void }) {
  const [url, setUrl] = useState('')
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const close = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    close.current?.focus()
    const controller = new AbortController()
    let objectUrl = ''
    async function load() {
      try {
        // Fetch through the authenticated SPA client, not a noreferrer tab navigation.
        const response = await apiClient.get<Blob>(`/patient/files/${file.id}/preview`, { responseType: 'blob', signal: controller.signal })
        if (controller.signal.aborted) return
        const type = response.data.type.split(';')[0]
        if (!['application/pdf', 'image/jpeg', 'image/png'].includes(type)) throw new Error('This file type cannot be previewed. Please download it instead.')
        objectUrl = URL.createObjectURL(response.data)
        setUrl(objectUrl)
      } catch (reason) {
        if (controller.signal.aborted) return
        const details = normalizeApiError(reason)
        setError(details.status === 404 ? 'The stored file could not be found. Please upload it again.' : details.status === 403 ? 'You do not have permission to preview this file.' : details.status === 401 ? 'Your session has expired. Please sign in again.' : details.message)
      }
    }
    void load()
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [file.id, attempt])
  return <section className="panel mt-3" aria-label="Private file preview" onKeyDown={event => { if (event.key === 'Escape') onClose() }}>
    <div className="panel-body">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3"><h2 className="h5 mb-0">{file.original_name}</h2><button ref={close} className="btn btn-outline-secondary" onClick={onClose}>Close preview</button></div>
      {error ? <div role="alert"><p>{error}</p><button className="btn btn-outline-primary" onClick={() => { setError(''); setUrl(''); setAttempt(value => value + 1) }}>Retry preview</button></div> : !url ? <p role="status">Loading private preview…</p> : file.mime_type === 'application/pdf' ? <><iframe title={`Preview: ${file.original_name}`} src={url} style={{ width: '100%', height: '65vh', border: 0 }} /><p>If your browser cannot display PDFs, <a href={url} download={file.original_name}>download this file</a>.</p></> : <img src={url} alt={`Preview: ${file.original_name}`} style={{ display: 'block', maxWidth: '100%', maxHeight: '65vh', margin: 'auto', objectFit: 'contain' }} />}
    </div>
  </section>
}
